import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit as fsLimit,
  startAfter,
  serverTimestamp,
  runTransaction,
  onSnapshot,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { COLLECTIONS, LOG_TYPES, VALIDATION, DEFAULT_FEEDING_INTERVAL_HOURS, LEARNING_STATUS, OPEN_BAG_MODES } from '../config/constants';
import { AppError, FirestoreError } from '../lib/errors';
import {
  createCycle,
  addFeedingToCycle,
  addRefillToCycle,
  closeCycle,
  incorporateClosedCycle,
  estimateRemainingFeedings,
  estimateDaysRemaining,
} from '../lib/learning';

// =====================================================================
// VALIDADORES
// =====================================================================

/**
 * Valida los campos de una mascota.
 * Lanza AppError con el primer error encontrado. UI puede validar en paralelo.
 * Stock y capacidad del dispenser viven en feedingGroups — acá solo pet-specific.
 */
const validatePetInput = ({ name, dailyRationGr, feedingsPerDay }) => {
  if (!name?.trim()) {
    throw new AppError('El nombre es requerido', 'INVALID_NAME');
  }
  if (name.length > VALIDATION.PET_NAME_MAX) {
    throw new AppError(`El nombre supera los ${VALIDATION.PET_NAME_MAX} caracteres`, 'INVALID_NAME');
  }
  if (
    !Number.isInteger(dailyRationGr) ||
    dailyRationGr < VALIDATION.DAILY_RATION_MIN_GR ||
    dailyRationGr > VALIDATION.DAILY_RATION_MAX_GR
  ) {
    throw new AppError(
      `La ración diaria debe ser un entero entre ${VALIDATION.DAILY_RATION_MIN_GR} y ${VALIDATION.DAILY_RATION_MAX_GR} g`,
      'INVALID_DAILY_RATION'
    );
  }
  if (
    !Number.isInteger(feedingsPerDay) ||
    feedingsPerDay < VALIDATION.FEEDINGS_PER_DAY_MIN ||
    feedingsPerDay > VALIDATION.FEEDINGS_PER_DAY_MAX
  ) {
    throw new AppError(
      `Las comidas por día deben ser un entero entre ${VALIDATION.FEEDINGS_PER_DAY_MIN} y ${VALIDATION.FEEDINGS_PER_DAY_MAX}`,
      'INVALID_FEEDINGS_PER_DAY'
    );
  }
};

// =====================================================================
// HELPERS DE DOMINIO
// =====================================================================

/**
 * Calcula la ración por comida (gramos por alimentación).
 * Redondeo a entero más cercano: descartar decimales acumula error a lo largo del día.
 */
export const computePortionGr = (dailyRationGr, feedingsPerDay) => {
  return Math.round(dailyRationGr / feedingsPerDay);
};

/**
 * Calcula el intervalo en horas entre comidas.
 * No persiste — derivable en cualquier momento desde feedingsPerDay.
 */
export const computeIntervalHours = (feedingsPerDay) => {
  if (!feedingsPerDay || feedingsPerDay <= 0) return DEFAULT_FEEDING_INTERVAL_HOURS;
  return 24 / feedingsPerDay;
};

/**
 * Genera un ID único para un ciclo.
 * Usamos crypto.randomUUID si está disponible (Node 19+ y todos los browsers
 * modernos). Fallback a timestamp + random para edge cases.
 */
const generateCycleId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `cyc_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
};

// =====================================================================
// CREATE / UPDATE / SOFT DELETE
// =====================================================================

/**
 * Crea una nueva mascota en el hogar.
 *
 * @param {object} params
 * @param {string} params.uid - Creador
 * @param {string} params.familyId - Hogar al que pertenece
 * @param {string} params.name
 * @param {string} [params.species]
 * @param {Date} [params.birthDate]
 * @param {number} params.dailyRationGr
 * @param {number} params.feedingsPerDay
 * @param {string} params.feedingGroupId - Grupo al que se asocia (debe existir y ser del mismo hogar)
 */
export const createPet = async ({
  uid,
  familyId,
  feedingGroupId,
  hydrationGroupId = null,
  name,
  species = null,
  birthDate = null,
  dailyRationGr,
  feedingsPerDay,
}) => {
  if (!uid) throw new AppError('Usuario requerido', 'INVALID_INPUT');
  if (!familyId) throw new AppError('Hogar requerido', 'INVALID_INPUT');
  if (!feedingGroupId) throw new AppError('Grupo de alimentación requerido', 'INVALID_INPUT');

  validatePetInput({ name, dailyRationGr, feedingsPerDay });

  try {
    const petRef = doc(collection(db, COLLECTIONS.PETS));

    await runTransaction(db, async (transaction) => {
      // Verificar que el usuario pertenece al hogar.
      const familyRef = doc(db, COLLECTIONS.FAMILIES, familyId);
      const familySnap = await transaction.get(familyRef);
      if (!familySnap.exists()) {
        throw new AppError('El hogar no existe', 'FAMILY_NOT_FOUND');
      }
      if (!familySnap.data().memberIds.includes(uid)) {
        throw new AppError('No pertenecés a este hogar', 'NOT_MEMBER');
      }

      // Verificar que el grupo existe y pertenece al mismo hogar.
      const groupRef = doc(db, COLLECTIONS.FEEDING_GROUPS, feedingGroupId);
      const groupSnap = await transaction.get(groupRef);
      if (!groupSnap.exists()) {
        throw new AppError('El grupo no existe', 'GROUP_NOT_FOUND');
      }
      const group = groupSnap.data();
      if (group.familyId !== familyId) {
        throw new AppError('El grupo no pertenece a este hogar', 'GROUP_FAMILY_MISMATCH');
      }
      if (group.deletedAt) {
        throw new AppError('El grupo fue eliminado', 'GROUP_DELETED');
      }

      // Si la pet opta por trackear hidratación, validamos que el balde
      // exista y sea del mismo hogar. Defense-in-depth: las rules también
      // bloquean tenant-hopping vía hydrationGroupId, pero acá damos error
      // user-facing más claro y atomicidad con el resto del create.
      if (hydrationGroupId) {
        const hydrationRef = doc(db, COLLECTIONS.HYDRATION_GROUPS, hydrationGroupId);
        const hydrationSnap = await transaction.get(hydrationRef);
        if (!hydrationSnap.exists()) {
          throw new AppError('El balde no existe', 'HYDRATION_GROUP_NOT_FOUND');
        }
        const hydrationGroup = hydrationSnap.data();
        if (hydrationGroup.familyId !== familyId) {
          throw new AppError(
            'El balde no pertenece a este hogar',
            'HYDRATION_GROUP_FAMILY_MISMATCH'
          );
        }
        if (hydrationGroup.deletedAt) {
          throw new AppError('El balde fue eliminado', 'HYDRATION_GROUP_DELETED');
        }
      }

      transaction.set(petRef, {
        familyId,
        feedingGroupId,
        hydrationGroupId: hydrationGroupId || null,
        name: name.trim(),
        species: species?.trim() || null,
        birthDate: birthDate || null,
        // photoURL queda como null y deprecado tras la migración 2026-05-19.
        // La foto de la pet vive en Supabase Storage (bucket privado) y el
        // cliente la pide al worker como signed URL temporal. Solo guardamos
        // `photoExt` para saber si la pet tiene foto y con qué extensión.
        photoURL: null,
        photoExt: null,

        dailyRationGr,
        feedingsPerDay,
        feedingIntervalHours: computeIntervalHours(feedingsPerDay),
        lastFed: null,
        lastFedBy: null,

        deletedAt: null,

        createdBy: uid,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
    });

    return { petId: petRef.id };
  } catch (err) {
    if (err instanceof AppError) throw err;
    if (err?.cause instanceof AppError) throw err.cause;
    throw new FirestoreError('No se pudo crear la mascota', err);
  }
};

/**
 * Actualiza campos editables de una mascota.
 * NO permite tocar stock — usar feedPet/refillDispenser para eso.
 */
export const updatePet = async ({ uid, petId, updates }) => {
  if (!uid) throw new AppError('Usuario requerido', 'INVALID_INPUT');
  if (!petId) throw new AppError('Mascota requerida', 'INVALID_INPUT');

  // Campos editables vía esta función. Stock y lastFed se gestionan aparte;
  // dispenserCapacityGr vive en el feedingGroup y se edita con updateFeedingGroup.
  // feedingGroupId se cambia con un endpoint dedicado (movePetToGroup).
  // hydrationGroupId puede ser string (activar/cambiar balde) o null (desactivar).
  const ALLOWED_FIELDS = [
    'name',
    'species',
    'birthDate',
    'photoURL',
    'photoExt',
    'dailyRationGr',
    'feedingsPerDay',
    'hydrationGroupId',
  ];

  const sanitized = {};
  for (const key of ALLOWED_FIELDS) {
    if (updates[key] !== undefined) sanitized[key] = updates[key];
  }
  if (Object.keys(sanitized).length === 0) {
    throw new AppError('No hay cambios para aplicar', 'NO_CHANGES');
  }

  // Si cambian datos de alimentación, recalculamos el intervalo.
  if (sanitized.feedingsPerDay !== undefined) {
    if (
      !Number.isInteger(sanitized.feedingsPerDay) ||
      sanitized.feedingsPerDay < VALIDATION.FEEDINGS_PER_DAY_MIN ||
      sanitized.feedingsPerDay > VALIDATION.FEEDINGS_PER_DAY_MAX
    ) {
      throw new AppError('feedingsPerDay inválido', 'INVALID_INPUT');
    }
    sanitized.feedingIntervalHours = computeIntervalHours(sanitized.feedingsPerDay);
  }

  if (sanitized.dailyRationGr !== undefined) {
    if (
      !Number.isInteger(sanitized.dailyRationGr) ||
      sanitized.dailyRationGr < VALIDATION.DAILY_RATION_MIN_GR ||
      sanitized.dailyRationGr > VALIDATION.DAILY_RATION_MAX_GR
    ) {
      throw new AppError('dailyRationGr inválido', 'INVALID_INPUT');
    }
  }

  if (sanitized.name !== undefined) {
    if (!sanitized.name?.trim()) {
      throw new AppError('El nombre no puede quedar vacío', 'INVALID_NAME');
    }
    sanitized.name = sanitized.name.trim();
  }

  // Sanity de hydrationGroupId: solo permitimos string no-vacío o null.
  // La validación cross-collection (existe + same family + no deletedAt)
  // ocurre dentro de la transacción.
  if (sanitized.hydrationGroupId !== undefined) {
    if (
      sanitized.hydrationGroupId !== null &&
      (typeof sanitized.hydrationGroupId !== 'string' || sanitized.hydrationGroupId.trim() === '')
    ) {
      throw new AppError('hydrationGroupId inválido', 'INVALID_INPUT');
    }
  }

  try {
    await runTransaction(db, async (transaction) => {
      const petRef = doc(db, COLLECTIONS.PETS, petId);
      const petSnap = await transaction.get(petRef);
      if (!petSnap.exists()) {
        throw new AppError('La mascota no existe', 'PET_NOT_FOUND');
      }
      const pet = petSnap.data();
      if (pet.deletedAt) {
        throw new AppError('La mascota fue eliminada', 'PET_DELETED');
      }

      // Si se está cambiando hydrationGroupId a un grupo, validar pertenencia.
      // El cambio puede ser: null→string (activar), string→string (cambiar
      // de balde), string→null (desactivar). Solo validamos cuando el destino
      // es un string.
      const prevHydrationGroupId = pet.hydrationGroupId ?? null;
      const nextHydrationGroupId = sanitized.hydrationGroupId === undefined
        ? prevHydrationGroupId
        : sanitized.hydrationGroupId;

      if (
        nextHydrationGroupId &&
        nextHydrationGroupId !== prevHydrationGroupId
      ) {
        const hydrationRef = doc(db, COLLECTIONS.HYDRATION_GROUPS, nextHydrationGroupId);
        const hydrationSnap = await transaction.get(hydrationRef);
        if (!hydrationSnap.exists()) {
          throw new AppError('El balde no existe', 'HYDRATION_GROUP_NOT_FOUND');
        }
        const hydrationGroup = hydrationSnap.data();
        if (hydrationGroup.familyId !== pet.familyId) {
          throw new AppError(
            'El balde no pertenece a este hogar',
            'HYDRATION_GROUP_FAMILY_MISMATCH'
          );
        }
        if (hydrationGroup.deletedAt) {
          throw new AppError('El balde fue eliminado', 'HYDRATION_GROUP_DELETED');
        }
      }

      transaction.update(petRef, {
        ...sanitized,
        updatedAt: serverTimestamp(),
      });

      // Logueo de cambios en hydrationGroupId. El log queda asociado al
      // nuevo balde (o al anterior, si se desactivó). Si no hubo cambio
      // real, no se loguea.
      if (
        sanitized.hydrationGroupId !== undefined &&
        nextHydrationGroupId !== prevHydrationGroupId
      ) {
        const isEnabling = !prevHydrationGroupId && nextHydrationGroupId;
        const isDisabling = prevHydrationGroupId && !nextHydrationGroupId;
        const isSwitching = prevHydrationGroupId && nextHydrationGroupId;

        const logRef = doc(collection(db, COLLECTIONS.LOGS));
        transaction.set(logRef, {
          familyId: pet.familyId,
          feedingGroupId: null,
          // Asociamos el log al balde RELEVANTE: el nuevo si se activó o se
          // cambió, el viejo si se desactivó. Así el evento aparece donde
          // "se siente" desde ese momento.
          hydrationGroupId: nextHydrationGroupId || prevHydrationGroupId,
          petId,
          userId: uid,
          type: LOG_TYPES.NOTE,
          timestamp: serverTimestamp(),
          payload: {
            reason: isEnabling
              ? 'hydration_enabled_for_pet'
              : isDisabling
              ? 'hydration_disabled_for_pet'
              : 'hydration_group_changed_for_pet',
            petName: pet.name,
            ...(isSwitching && {
              previousGroupId: prevHydrationGroupId,
              newGroupId: nextHydrationGroupId,
            }),
          },
        });
      }
    });
  } catch (err) {
    if (err instanceof AppError) throw err;
    if (err?.cause instanceof AppError) throw err.cause;
    throw new FirestoreError('No se pudo actualizar la mascota', err);
  }
};

/**
 * Mueve una mascota a otro grupo de alimentación del mismo hogar.
 * Caso de uso: dieta especial → la mascota deja de compartir dispenser
 * con sus compañeras y pasa a otro grupo (o uno nuevo).
 *
 * Atómico: lee pet + grupo destino, valida que el grupo es del mismo
 * familyId y no está soft-deleted, actualiza feedingGroupId, deja log.
 *
 * NO toca el grupo origen (su stock se queda como estaba). Si la pet
 * que se movió era la única del grupo origen, ese grupo queda huérfano
 * — el usuario puede eliminarlo desde la UI (softDeleteFeedingGroup).
 */
export const movePetToGroup = async ({ uid, petId, newFeedingGroupId }) => {
  if (!uid) throw new AppError('Usuario requerido', 'INVALID_INPUT');
  if (!petId) throw new AppError('Mascota requerida', 'INVALID_INPUT');
  if (!newFeedingGroupId) throw new AppError('Grupo destino requerido', 'INVALID_INPUT');

  try {
    await runTransaction(db, async (transaction) => {
      const petRef = doc(db, COLLECTIONS.PETS, petId);
      const petSnap = await transaction.get(petRef);
      if (!petSnap.exists()) {
        throw new AppError('La mascota no existe', 'PET_NOT_FOUND');
      }
      const pet = petSnap.data();
      if (pet.deletedAt) {
        throw new AppError('La mascota fue eliminada', 'PET_DELETED');
      }
      if (pet.feedingGroupId === newFeedingGroupId) {
        throw new AppError('La mascota ya está en ese grupo', 'SAME_GROUP');
      }

      const newGroupRef = doc(db, COLLECTIONS.FEEDING_GROUPS, newFeedingGroupId);
      const newGroupSnap = await transaction.get(newGroupRef);
      if (!newGroupSnap.exists()) {
        throw new AppError('El grupo destino no existe', 'GROUP_NOT_FOUND');
      }
      const newGroup = newGroupSnap.data();
      if (newGroup.familyId !== pet.familyId) {
        throw new AppError(
          'El grupo destino no pertenece a este hogar',
          'GROUP_FAMILY_MISMATCH'
        );
      }
      if (newGroup.deletedAt) {
        throw new AppError('El grupo destino fue eliminado', 'GROUP_DELETED');
      }

      const oldFeedingGroupId = pet.feedingGroupId;

      transaction.update(petRef, {
        feedingGroupId: newFeedingGroupId,
        updatedAt: serverTimestamp(),
      });

      // El log se asocia al grupo NUEVO (es donde el evento "se siente" desde ahora).
      // Pero guardamos también el fromGroupId en el payload para auditoría.
      const logRef = doc(collection(db, COLLECTIONS.LOGS));
      transaction.set(logRef, {
        familyId: pet.familyId,
        feedingGroupId: newFeedingGroupId,
        petId,
        userId: uid,
        type: LOG_TYPES.NOTE,
        timestamp: serverTimestamp(),
        payload: {
          reason: 'pet_moved_to_group',
          petName: pet.name,
          fromGroupId: oldFeedingGroupId ?? null,
          toGroupId: newFeedingGroupId,
        },
      });
    });
  } catch (err) {
    if (err instanceof AppError) throw err;
    if (err?.cause instanceof AppError) throw err.cause;
    throw new FirestoreError('No se pudo mover la mascota de grupo', err);
  }
};

/**
 * Soft delete: marca deletedAt. Los logs y referencias históricas
 * se mantienen para preservar historial emocional.
 */
export const softDeletePet = async ({ uid, petId }) => {
  try {
    await runTransaction(db, async (transaction) => {
      const petRef = doc(db, COLLECTIONS.PETS, petId);
      const petSnap = await transaction.get(petRef);
      if (!petSnap.exists()) {
        throw new AppError('La mascota no existe', 'PET_NOT_FOUND');
      }
      if (petSnap.data().deletedAt) {
        throw new AppError('Ya fue eliminada', 'ALREADY_DELETED');
      }

      transaction.update(petRef, {
        deletedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      // Log para trazabilidad
      const logRef = doc(collection(db, COLLECTIONS.LOGS));
      transaction.set(logRef, {
        familyId: petSnap.data().familyId,
        feedingGroupId: petSnap.data().feedingGroupId ?? null,
        petId,
        userId: uid,
        type: LOG_TYPES.NOTE,
        timestamp: serverTimestamp(),
        payload: {
          reason: 'pet_deleted',
          petName: petSnap.data().name,
        },
      });
    });
  } catch (err) {
    if (err instanceof AppError) throw err;
    if (err?.cause instanceof AppError) throw err.cause;
    throw new FirestoreError('No se pudo eliminar la mascota', err);
  }
};

// =====================================================================
// ALIMENTACIÓN
// =====================================================================

/**
 * Selecciona el dispenser desde el cual descontar una porción.
 * Estrategia: el de mayor stock que cubra portionGr (mantiene "feed de 1 tap").
 * Retorna null si ninguno alcanza para la porción.
 */
const selectDispenserForFeed = (dispensers, portionGr) => {
  if (!Array.isArray(dispensers) || dispensers.length === 0) return null;
  const candidates = dispensers
    .filter((d) => (d.stockGr ?? 0) >= portionGr)
    .sort((a, b) => (b.stockGr ?? 0) - (a.stockGr ?? 0));
  return candidates[0] ?? null;
};

/**
 * Alimenta a una mascota.
 *
 * Transacción atómica:
 *  1. Lee pet + grupo.
 *  2. Calcula porción (dailyRationGr / feedingsPerDay).
 *  3. Resolución del stock según el modelo de dispensers:
 *      - group.dispensers.length === 0 → "modo sin dispenser": descuenta de mainStockGr.
 *      - group.dispensers.length >= 1 → auto-select del que tenga más stock
 *        que cubra portionGr. Descuenta de ese dispenser específico.
 *  4. Updates pet.lastFed/lastFedBy + grupo (stock + activeCycle).
 *  5. Log FEEDING con dispenserId en el payload cuando aplica.
 *  6. Sin stock + forceFeed=true → MANUAL_FEEDING sin tocar stock ni ciclo.
 *
 * @returns {object} { portionGr, forced, dispenserId?, dispenserAfter?, mainAfter? }
 */
export const feedPet = async ({ uid, petId, forceFeed = false }) => {
  if (!uid) throw new AppError('Usuario requerido', 'INVALID_INPUT');
  if (!petId) throw new AppError('Mascota requerida', 'INVALID_INPUT');

  try {
    const result = await runTransaction(db, async (transaction) => {
      const petRef = doc(db, COLLECTIONS.PETS, petId);
      const petSnap = await transaction.get(petRef);
      if (!petSnap.exists()) {
        throw new AppError('La mascota no existe', 'PET_NOT_FOUND');
      }

      const pet = petSnap.data();
      if (pet.deletedAt) {
        throw new AppError('La mascota fue eliminada', 'PET_DELETED');
      }
      if (!pet.feedingGroupId) {
        throw new AppError('La mascota no está asignada a un grupo', 'PET_WITHOUT_GROUP');
      }

      const groupRef = doc(db, COLLECTIONS.FEEDING_GROUPS, pet.feedingGroupId);
      const groupSnap = await transaction.get(groupRef);
      if (!groupSnap.exists()) {
        throw new AppError('El grupo no existe', 'GROUP_NOT_FOUND');
      }
      const group = groupSnap.data();
      if (group.deletedAt) {
        throw new AppError('El grupo fue eliminado', 'GROUP_DELETED');
      }

      const portionGr = computePortionGr(pet.dailyRationGr, pet.feedingsPerDay);
      const dispensers = group.dispensers ?? [];
      const usesDispenser = dispensers.length > 0;

      // Resolución del dispenser candidato (cuando hay).
      const chosenDispenser = usesDispenser
        ? selectDispenserForFeed(dispensers, portionGr)
        : null;
      const hasEnoughStock = usesDispenser
        ? chosenDispenser != null
        : (group.mainStockGr ?? 0) >= portionGr;

      if (!hasEnoughStock && !forceFeed) {
        throw new AppError(
          usesDispenser
            ? 'Stock insuficiente en los dispensers'
            : 'Stock insuficiente en la bolsa principal',
          'INSUFFICIENT_STOCK'
        );
      }

      const logRef = doc(collection(db, COLLECTIONS.LOGS));

      if (hasEnoughStock) {
        // activeCycle: si no hay, lo creamos al primer feedPet
        // (edge case: el grupo recién creado y la mascota arranca a comer
        // antes de cualquier refill / openBag).
        const updatedActiveCycle = group.activeCycle
          ? addFeedingToCycle(group.activeCycle, portionGr)
          : addFeedingToCycle(
            createCycle({
              cycleId: generateCycleId(),
              startedAt: new Date(),
              refillAmountGr: 0,
            }),
            portionGr
          );

        const groupUpdates = {
          activeCycle: updatedActiveCycle,
          updatedAt: serverTimestamp(),
        };

        const logPayload = {
          amountGr: portionGr,
          cycleId: updatedActiveCycle.cycleId,
          source: usesDispenser ? 'dispenser' : 'main_bag',
        };

        if (usesDispenser) {
          // Descontar del dispenser elegido (immutable update del array).
          const newStock = (chosenDispenser.stockGr ?? 0) - portionGr;
          groupUpdates.dispensers = dispensers.map((d) =>
            d.id === chosenDispenser.id ? { ...d, stockGr: newStock } : d
          );
          logPayload.dispenserId = chosenDispenser.id;
          logPayload.dispenserName = chosenDispenser.name;
          logPayload.dispenserBefore = chosenDispenser.stockGr ?? 0;
          logPayload.dispenserAfter = newStock;
        } else {
          const newMain = (group.mainStockGr ?? 0) - portionGr;
          groupUpdates.mainStockGr = newMain;
          logPayload.mainBefore = group.mainStockGr ?? 0;
          logPayload.mainAfter = newMain;
        }

        transaction.update(groupRef, groupUpdates);
        transaction.update(petRef, {
          lastFed: serverTimestamp(),
          lastFedBy: uid,
          updatedAt: serverTimestamp(),
        });
        transaction.set(logRef, {
          familyId: pet.familyId,
          feedingGroupId: pet.feedingGroupId,
          petId,
          userId: uid,
          type: LOG_TYPES.FEEDING,
          timestamp: serverTimestamp(),
          payload: logPayload,
        });

        return {
          portionGr,
          forced: false,
          dispenserId: usesDispenser ? chosenDispenser.id : null,
          dispenserAfter: usesDispenser ? (chosenDispenser.stockGr ?? 0) - portionGr : null,
          mainAfter: usesDispenser ? (group.mainStockGr ?? 0) : (group.mainStockGr ?? 0) - portionGr,
        };
      } else {
        // forceFeed sin stock: solo logueamos, no tocamos stock ni ciclo.
        transaction.update(petRef, {
          lastFed: serverTimestamp(),
          lastFedBy: uid,
          updatedAt: serverTimestamp(),
        });
        const availableForLog = usesDispenser
          ? dispensers.reduce((max, d) => Math.max(max, d.stockGr ?? 0), 0)
          : (group.mainStockGr ?? 0);
        transaction.set(logRef, {
          familyId: pet.familyId,
          feedingGroupId: pet.feedingGroupId,
          petId,
          userId: uid,
          type: LOG_TYPES.MANUAL_FEEDING,
          timestamp: serverTimestamp(),
          payload: {
            intendedAmountGr: portionGr,
            stockAtFeedGr: availableForLog,
            source: usesDispenser ? 'dispenser' : 'main_bag',
            reason: 'out_of_stock',
          },
        });
        return {
          portionGr,
          forced: true,
          dispenserId: null,
          dispenserAfter: null,
          mainAfter: group.mainStockGr ?? 0,
        };
      }
    });

    return result;
  } catch (err) {
    if (err instanceof AppError) throw err;
    if (err?.cause instanceof AppError) throw err.cause;
    throw new FirestoreError('No se pudo alimentar a la mascota', err);
  }
};

// =====================================================================
// INVENTARIO
// =====================================================================

/**
 * Rellena un dispenser específico desde la bolsa principal.
 *
 * Stage 2: requiere `dispenserId` explícito (cuál de los N rellenar). Si el
 * grupo tiene 0 dispensers, rechaza (no aplica la operación). Si tiene 1,
 * la UI puede auto-seleccionar ese id.
 *
 * `wasEmpty` indica que el dispenser estaba vacío antes de cargar (señal humana,
 * autoritativa frente al sistema). Cuando es true:
 *  - El cálculo de capacidad usa 0 como base.
 *  - Si había un activeCycle, se CIERRA (con outlier detection + EMA),
 *    y se abre uno nuevo con este refill como inicial.
 *  - El stock previo del dispenser (si lo había) se descarta como discrepancia
 *    y queda registrado en el payload del log.
 *
 * Cuando es false (default): refill parcial que ACUMULA en el activeCycle.
 */
export const refillDispenser = async ({ uid, feedingGroupId, dispenserId, amountGr, wasEmpty = false }) => {
  if (!uid) throw new AppError('Usuario requerido', 'INVALID_INPUT');
  if (!feedingGroupId) throw new AppError('Grupo requerido', 'INVALID_INPUT');
  if (!dispenserId) throw new AppError('Dispenser requerido', 'INVALID_INPUT');
  if (!Number.isInteger(amountGr) || amountGr <= 0) {
    throw new AppError('La cantidad debe ser un entero positivo', 'INVALID_AMOUNT');
  }

  try {
    const result = await runTransaction(db, async (transaction) => {
      const groupRef = doc(db, COLLECTIONS.FEEDING_GROUPS, feedingGroupId);
      const groupSnap = await transaction.get(groupRef);
      if (!groupSnap.exists()) throw new AppError('El grupo no existe', 'GROUP_NOT_FOUND');

      const group = groupSnap.data();
      if (group.deletedAt) throw new AppError('El grupo fue eliminado', 'GROUP_DELETED');

      const dispensers = group.dispensers ?? [];
      if (dispensers.length === 0) {
        throw new AppError(
          'Este grupo no usa dispenser. La mascota come directo de la bolsa.',
          'GROUP_WITHOUT_DISPENSER'
        );
      }

      const targetDispenser = dispensers.find((d) => d.id === dispenserId);
      if (!targetDispenser) {
        throw new AppError(
          'El dispenser no existe en este grupo',
          'DISPENSER_NOT_FOUND'
        );
      }

      if (group.mainStockGr < amountGr) {
        throw new AppError(
          `La bolsa principal solo tiene ${group.mainStockGr} g`,
          'INSUFFICIENT_MAIN_STOCK'
        );
      }

      // wasEmpty resetea la base a 0. La discrepancia (si la había) se loguea.
      const dispenserBaseGr = wasEmpty ? 0 : (targetDispenser.stockGr ?? 0);
      const dispenserDiscrepancyGr = wasEmpty ? (targetDispenser.stockGr ?? 0) : 0;

      const newDispenserStock = dispenserBaseGr + amountGr;
      if (newDispenserStock > targetDispenser.capacityGr) {
        throw new AppError(
          `Excede la capacidad del dispenser "${targetDispenser.name}" (${targetDispenser.capacityGr} g)`,
          'EXCEEDS_CAPACITY'
        );
      }

      // Cierre de ciclo (solo si wasEmpty y había activeCycle).
      let learningResult = null;
      if (wasEmpty && group.activeCycle) {
        const closed = closeCycle(group.activeCycle, new Date());
        learningResult = incorporateClosedCycle(closed, {
          cycles: group.cycles || [],
          avgFeedingsPerRefill: group.avgFeedingsPerRefill ?? null,
          avgRefillsPerBag: group.avgRefillsPerBag ?? null,
          cyclesCompleted: group.cyclesCompleted ?? 0,
        });
      }

      // activeCycle resultante (sigue siendo por grupo, no por dispenser).
      const nextActiveCycle = wasEmpty
        ? createCycle({
          cycleId: generateCycleId(),
          startedAt: new Date(),
          refillAmountGr: amountGr,
        })
        : group.activeCycle
          ? addRefillToCycle(group.activeCycle, amountGr)
          : createCycle({
            cycleId: generateCycleId(),
            startedAt: new Date(),
            refillAmountGr: amountGr,
          });

      // Update inmutable del array de dispensers.
      const updatedDispensers = dispensers.map((d) =>
        d.id === dispenserId ? { ...d, stockGr: newDispenserStock } : d
      );

      const updates = {
        mainStockGr: group.mainStockGr - amountGr,
        dispensers: updatedDispensers,
        activeCycle: nextActiveCycle,
        updatedAt: serverTimestamp(),
      };

      if (learningResult) {
        updates.cycles = learningResult.cycles;
        updates.avgFeedingsPerRefill = learningResult.avgFeedingsPerRefill;
        updates.avgRefillsPerBag = learningResult.avgRefillsPerBag;
        updates.cyclesCompleted = learningResult.cyclesCompleted;
        updates.learningStatus = learningResult.learningStatus;
      }

      transaction.update(groupRef, updates);

      const isPartialRefill = !wasEmpty && Boolean(group.activeCycle);

      const logRef = doc(collection(db, COLLECTIONS.LOGS));
      transaction.set(logRef, {
        familyId: group.familyId,
        feedingGroupId,
        petId: null,
        userId: uid,
        type: LOG_TYPES.REFILL,
        timestamp: serverTimestamp(),
        payload: {
          amountGr,
          mainBefore: group.mainStockGr,
          mainAfter: group.mainStockGr - amountGr,
          dispenserId,
          dispenserName: targetDispenser.name,
          dispenserBefore: targetDispenser.stockGr ?? 0,
          dispenserAfter: newDispenserStock,
          cycleId: nextActiveCycle.cycleId,
          isPartialRefill,
          wasEmpty,
          dispenserDiscrepancyGr,
          closedCycleId: learningResult?.closedCycle.cycleId ?? null,
          closedCycleFeedings: learningResult?.closedCycle.feedingsCount ?? null,
          closedCycleWasOutlier: learningResult?.closedCycle.isOutlier ?? false,
        },
      });

      return {
        mainStockGr: group.mainStockGr - amountGr,
        dispenserId,
        dispenserStockGr: newDispenserStock,
        cycleId: nextActiveCycle.cycleId,
        isPartialRefill,
        closedCycleId: learningResult?.closedCycle.cycleId ?? null,
        closedCycleWasOutlier: learningResult?.closedCycle.isOutlier ?? false,
        learningStatus: learningResult?.learningStatus ?? group.learningStatus,
      };
    });

    return result;
  } catch (err) {
    if (err instanceof AppError) throw err;
    if (err?.cause instanceof AppError) throw err.cause;
    throw new FirestoreError('No se pudo rellenar el dispenser', err);
  }
};

/**
 * Suma stock a la bolsa principal — ajuste manual / escape hatch.
 * Útil para casos donde el modelo de bolsas cerradas no encaja
 * (ej: te regalaron media bolsa abierta). El flujo principal de
 * "comprar bolsas" usa addExtraBags + openBag.
 *
 * No descuenta de ningún lado — es entrada de inventario.
 */
export const addMainStock = async ({ uid, feedingGroupId, amountGr }) => {
  if (!uid) throw new AppError('Usuario requerido', 'INVALID_INPUT');
  if (!feedingGroupId) throw new AppError('Grupo requerido', 'INVALID_INPUT');
  if (!Number.isInteger(amountGr) || amountGr <= 0) {
    throw new AppError('La cantidad debe ser un entero positivo', 'INVALID_AMOUNT');
  }

  try {
    await runTransaction(db, async (transaction) => {
      const groupRef = doc(db, COLLECTIONS.FEEDING_GROUPS, feedingGroupId);
      const groupSnap = await transaction.get(groupRef);
      if (!groupSnap.exists()) throw new AppError('El grupo no existe', 'GROUP_NOT_FOUND');

      const group = groupSnap.data();
      if (group.deletedAt) throw new AppError('El grupo fue eliminado', 'GROUP_DELETED');

      const newMain = group.mainStockGr + amountGr;
      if (newMain > VALIDATION.MAIN_STOCK_MAX_GR) {
        throw new AppError(
          `El stock total excede el máximo permitido`,
          'EXCEEDS_MAX_STOCK'
        );
      }

      transaction.update(groupRef, {
        mainStockGr: newMain,
        updatedAt: serverTimestamp(),
      });

      const logRef = doc(collection(db, COLLECTIONS.LOGS));
      transaction.set(logRef, {
        familyId: group.familyId,
        feedingGroupId,
        petId: null,
        userId: uid,
        type: LOG_TYPES.MAIN_STOCK_ADD,
        timestamp: serverTimestamp(),
        payload: {
          amountGr,
          mainBefore: group.mainStockGr,
          mainAfter: newMain,
        },
      });
    });
  } catch (err) {
    if (err instanceof AppError) throw err;
    if (err?.cause instanceof AppError) throw err.cause;
    throw new FirestoreError('No se pudo agregar stock', err);
  }
};

/**
 * Suma bolsas cerradas a la reserva. Si bagCapacityGr se pasa, también
 * actualiza la capacidad de bolsa del grupo (caso típico: el usuario compra
 * por primera vez y todavía no había setteado la capacidad). Si no se pasa,
 * el grupo debe tener bagCapacityGr previamente seteado.
 *
 * Genera un log BAGS_BOUGHT para trazabilidad.
 */
export const addExtraBags = async ({ uid, feedingGroupId, count, bagCapacityGr }) => {
  if (!uid) throw new AppError('Usuario requerido', 'INVALID_INPUT');
  if (!feedingGroupId) throw new AppError('Grupo requerido', 'INVALID_INPUT');
  if (!Number.isInteger(count) || count <= 0) {
    throw new AppError('La cantidad de bolsas debe ser un entero positivo', 'INVALID_COUNT');
  }
  if (bagCapacityGr != null) {
    if (
      !Number.isInteger(bagCapacityGr) ||
      bagCapacityGr < VALIDATION.BAG_CAPACITY_MIN_GR ||
      bagCapacityGr > VALIDATION.BAG_CAPACITY_MAX_GR
    ) {
      throw new AppError(
        `La capacidad de la bolsa debe ser un entero entre ${VALIDATION.BAG_CAPACITY_MIN_GR} y ${VALIDATION.BAG_CAPACITY_MAX_GR} g`,
        'INVALID_BAG_CAPACITY'
      );
    }
  }

  try {
    const result = await runTransaction(db, async (transaction) => {
      const groupRef = doc(db, COLLECTIONS.FEEDING_GROUPS, feedingGroupId);
      const groupSnap = await transaction.get(groupRef);
      if (!groupSnap.exists()) throw new AppError('El grupo no existe', 'GROUP_NOT_FOUND');

      const group = groupSnap.data();
      if (group.deletedAt) throw new AppError('El grupo fue eliminado', 'GROUP_DELETED');

      const effectiveBagCapacity = bagCapacityGr ?? group.bagCapacityGr;
      if (effectiveBagCapacity == null) {
        throw new AppError(
          'Tenés que indicar la capacidad de la bolsa (la primera vez)',
          'MISSING_BAG_CAPACITY'
        );
      }

      // Si ya hay bolsas en reserva con una capacidad distinta a la que se intenta
      // setear, rechazamos. Si dejáramos pasar el cambio, las bolsas viejas
      // pasarían a "valer" el nuevo tamaño al abrirlas — corrompe el inventario.
      // Caso de uso real: para cambiar tamaño primero hay que usar las bolsas viejas.
      const currentCount = group.extraBagsCount ?? 0;
      if (
        bagCapacityGr != null &&
        group.bagCapacityGr != null &&
        bagCapacityGr !== group.bagCapacityGr &&
        currentCount > 0
      ) {
        throw new AppError(
          `Ya tenés ${currentCount} bolsa${currentCount === 1 ? '' : 's'} de ${group.bagCapacityGr} g en reserva. Para registrar un tamaño distinto, primero abrí las que tenés guardadas.`,
          'BAG_CAPACITY_MISMATCH'
        );
      }

      const newCount = currentCount + count;
      if (newCount > VALIDATION.EXTRA_BAGS_MAX) {
        throw new AppError(
          `Excede el máximo de bolsas en reserva (${VALIDATION.EXTRA_BAGS_MAX})`,
          'EXCEEDS_MAX_BAGS'
        );
      }

      const updates = {
        extraBagsCount: newCount,
        updatedAt: serverTimestamp(),
      };
      if (bagCapacityGr != null && bagCapacityGr !== group.bagCapacityGr) {
        updates.bagCapacityGr = bagCapacityGr;
      }

      transaction.update(groupRef, updates);

      const logRef = doc(collection(db, COLLECTIONS.LOGS));
      transaction.set(logRef, {
        familyId: group.familyId,
        feedingGroupId,
        petId: null,
        userId: uid,
        type: LOG_TYPES.BAGS_BOUGHT,
        timestamp: serverTimestamp(),
        payload: {
          count,
          extraBagsBefore: currentCount,
          extraBagsAfter: newCount,
          bagCapacityGr: effectiveBagCapacity,
          bagCapacityChanged: bagCapacityGr != null && bagCapacityGr !== group.bagCapacityGr,
        },
      });

      return { extraBagsCount: newCount, bagCapacityGr: effectiveBagCapacity };
    });
    return result;
  } catch (err) {
    if (err instanceof AppError) throw err;
    if (err?.cause instanceof AppError) throw err.cause;
    throw new FirestoreError('No se pudo registrar la compra de bolsas', err);
  }
};

/**
 * Abre una bolsa cerrada de la reserva → mainStock.
 *
 * Modos:
 *  - PREVIOUS_EMPTY: la anterior se vació. mainStock = bagCapacityGr.
 *    Si dispenserStockGr (del mainStock) era > 0, el sistema creía mal —
 *    se descarta como discrepancia y se loguea.
 *  - ADD_TO_CURRENT: se suma a lo que quedaba. mainStock += bagCapacityGr.
 *    Se respeta el cap MAIN_STOCK_MAX_GR.
 *
 * Requiere extraBagsCount > 0 y bagCapacityGr seteado.
 */
export const openBag = async ({ uid, feedingGroupId, mode = OPEN_BAG_MODES.PREVIOUS_EMPTY }) => {
  if (!uid) throw new AppError('Usuario requerido', 'INVALID_INPUT');
  if (!feedingGroupId) throw new AppError('Grupo requerido', 'INVALID_INPUT');
  if (mode !== OPEN_BAG_MODES.PREVIOUS_EMPTY && mode !== OPEN_BAG_MODES.ADD_TO_CURRENT) {
    throw new AppError('Modo de apertura inválido', 'INVALID_MODE');
  }

  try {
    const result = await runTransaction(db, async (transaction) => {
      const groupRef = doc(db, COLLECTIONS.FEEDING_GROUPS, feedingGroupId);
      const groupSnap = await transaction.get(groupRef);
      if (!groupSnap.exists()) throw new AppError('El grupo no existe', 'GROUP_NOT_FOUND');

      const group = groupSnap.data();
      if (group.deletedAt) throw new AppError('El grupo fue eliminado', 'GROUP_DELETED');

      if ((group.extraBagsCount ?? 0) <= 0) {
        throw new AppError('No hay bolsas en reserva', 'NO_BAGS_IN_RESERVE');
      }
      if (group.bagCapacityGr == null) {
        throw new AppError(
          'Falta definir la capacidad de la bolsa para este grupo',
          'MISSING_BAG_CAPACITY'
        );
      }

      const bagCap = group.bagCapacityGr;
      let newMain;
      let mainDiscardedGr = 0;
      if (mode === OPEN_BAG_MODES.PREVIOUS_EMPTY) {
        // La anterior se vació: lo que el sistema creía que había es discrepancia.
        mainDiscardedGr = group.mainStockGr;
        newMain = bagCap;
      } else {
        // Suma al stock que quedaba — respetar el cap.
        newMain = group.mainStockGr + bagCap;
        if (newMain > VALIDATION.MAIN_STOCK_MAX_GR) {
          throw new AppError(
            `El total resultante (${newMain} g) supera el máximo de stock`,
            'EXCEEDS_MAX_STOCK'
          );
        }
      }

      const newExtraBags = (group.extraBagsCount ?? 0) - 1;

      transaction.update(groupRef, {
        mainStockGr: newMain,
        extraBagsCount: newExtraBags,
        updatedAt: serverTimestamp(),
      });

      const logRef = doc(collection(db, COLLECTIONS.LOGS));
      transaction.set(logRef, {
        familyId: group.familyId,
        feedingGroupId,
        petId: null,
        userId: uid,
        type: LOG_TYPES.BAG_OPENED,
        timestamp: serverTimestamp(),
        payload: {
          mode,
          bagCapacityGr: bagCap,
          mainBefore: group.mainStockGr,
          mainAfter: newMain,
          mainDiscardedGr,
          extraBagsBefore: group.extraBagsCount ?? 0,
          extraBagsAfter: newExtraBags,
        },
      });

      return {
        mainStockGr: newMain,
        extraBagsCount: newExtraBags,
        mainDiscardedGr,
      };
    });
    return result;
  } catch (err) {
    if (err instanceof AppError) throw err;
    if (err?.cause instanceof AppError) throw err.cause;
    throw new FirestoreError('No se pudo abrir la bolsa', err);
  }
};

/**
 * Confirmación humana de que un dispenser específico está vacío.
 *
 * Stage 2: requiere `dispenserId`. Resetea el stockGr de ese dispenser a 0
 * y, si había un activeCycle, lo cierra (el cierre sigue siendo a nivel grupo
 * porque el aprendizaje es por grupo).
 *
 * Idempotente: si el dispenser ya estaba en 0 y no hay activeCycle, no-op.
 */
export const confirmEmpty = async ({ uid, feedingGroupId, dispenserId }) => {
  if (!uid) throw new AppError('Usuario requerido', 'INVALID_INPUT');
  if (!feedingGroupId) throw new AppError('Grupo requerido', 'INVALID_INPUT');
  if (!dispenserId) throw new AppError('Dispenser requerido', 'INVALID_INPUT');

  try {
    const result = await runTransaction(db, async (transaction) => {
      const groupRef = doc(db, COLLECTIONS.FEEDING_GROUPS, feedingGroupId);
      const groupSnap = await transaction.get(groupRef);
      if (!groupSnap.exists()) throw new AppError('El grupo no existe', 'GROUP_NOT_FOUND');

      const group = groupSnap.data();
      if (group.deletedAt) throw new AppError('El grupo fue eliminado', 'GROUP_DELETED');

      const dispensers = group.dispensers ?? [];
      if (dispensers.length === 0) {
        throw new AppError(
          'Este grupo no usa dispenser. No hay nada que marcar vacío.',
          'GROUP_WITHOUT_DISPENSER'
        );
      }

      const targetDispenser = dispensers.find((d) => d.id === dispenserId);
      if (!targetDispenser) {
        throw new AppError('El dispenser no existe en este grupo', 'DISPENSER_NOT_FOUND');
      }

      const before = targetDispenser.stockGr ?? 0;

      // Idempotente: si ya estaba en 0 Y no hay ciclo activo, no-op.
      if (before === 0 && !group.activeCycle) {
        return { closed: false, learningStatus: group.learningStatus };
      }

      const updatedDispensers = dispensers.map((d) =>
        d.id === dispenserId ? { ...d, stockGr: 0 } : d
      );

      let updates = {
        dispensers: updatedDispensers,
        updatedAt: serverTimestamp(),
      };

      let learningResult = null;
      if (group.activeCycle) {
        const closed = closeCycle(group.activeCycle, new Date());
        learningResult = incorporateClosedCycle(closed, {
          cycles: group.cycles || [],
          avgFeedingsPerRefill: group.avgFeedingsPerRefill ?? null,
          avgRefillsPerBag: group.avgRefillsPerBag ?? null,
          cyclesCompleted: group.cyclesCompleted ?? 0,
        });

        updates = {
          ...updates,
          cycles: learningResult.cycles,
          avgFeedingsPerRefill: learningResult.avgFeedingsPerRefill,
          avgRefillsPerBag: learningResult.avgRefillsPerBag,
          cyclesCompleted: learningResult.cyclesCompleted,
          learningStatus: learningResult.learningStatus,
          activeCycle: null,
        };
      }

      transaction.update(groupRef, updates);

      const logRef = doc(collection(db, COLLECTIONS.LOGS));
      transaction.set(logRef, {
        familyId: group.familyId,
        feedingGroupId,
        petId: null,
        userId: uid,
        type: LOG_TYPES.CONFIRM_EMPTY,
        timestamp: serverTimestamp(),
        payload: {
          dispenserId,
          dispenserName: targetDispenser.name,
          dispenserBefore: before,
          dispenserAfter: 0,
          cycleId: group.activeCycle?.cycleId ?? null,
          cycleFeedings: group.activeCycle?.feedingsCount ?? null,
          cycleRefills: group.activeCycle?.refillAmountGr ?? null,
          isOutlier: learningResult?.closedCycle.isOutlier ?? false,
          outlierReason: learningResult?.closedCycle.outlierReason ?? null,
        },
      });

      return {
        closed: !!group.activeCycle,
        learningStatus: learningResult?.learningStatus ?? group.learningStatus,
        wasOutlier: learningResult?.closedCycle.isOutlier ?? false,
      };
    });

    return result;
  } catch (err) {
    if (err instanceof AppError) throw err;
    if (err?.cause instanceof AppError) throw err.cause;
    throw new FirestoreError('No se pudo confirmar el vaciado', err);
  }
};

/**
 * Ajuste manual del stock — escape hatch para discrepancias.
 * Útil cuando el sistema y la realidad divergen.
 * Crea log con la diferencia para trazabilidad.
 *
 * En Fase 3.B este endpoint será una de las puertas de calibración del algoritmo.
 */
/**
 * Ajusta el stock real cuando difiere del registrado.
 *
 * Stage 2: para ajustar un dispenser específico, pasar `dispenserId` + `dispenserStockGr`.
 * Para ajustar la bolsa principal, pasar `mainStockGr`. Se pueden combinar (un solo log).
 *
 * Si el grupo no tiene dispensers (modo sin dispenser), pasar `dispenserStockGr`
 * con o sin id es inválido.
 */
export const syncPhysicalStock = async ({
  uid,
  feedingGroupId,
  mainStockGr,
  dispenserId,
  dispenserStockGr,
  reason = 'manual_adjustment',
}) => {
  if (!uid) throw new AppError('Usuario requerido', 'INVALID_INPUT');
  if (!feedingGroupId) throw new AppError('Grupo requerido', 'INVALID_INPUT');
  if (mainStockGr !== undefined && (!Number.isInteger(mainStockGr) || mainStockGr < 0)) {
    throw new AppError('mainStockGr inválido', 'INVALID_INPUT');
  }
  if (dispenserStockGr !== undefined && (!Number.isInteger(dispenserStockGr) || dispenserStockGr < 0)) {
    throw new AppError('dispenserStockGr inválido', 'INVALID_INPUT');
  }
  if (dispenserStockGr !== undefined && !dispenserId) {
    throw new AppError('Para ajustar un dispenser hay que indicar dispenserId', 'INVALID_INPUT');
  }
  if (mainStockGr === undefined && dispenserStockGr === undefined) {
    throw new AppError('Nada para ajustar', 'NO_CHANGES');
  }

  try {
    await runTransaction(db, async (transaction) => {
      const groupRef = doc(db, COLLECTIONS.FEEDING_GROUPS, feedingGroupId);
      const groupSnap = await transaction.get(groupRef);
      if (!groupSnap.exists()) throw new AppError('El grupo no existe', 'GROUP_NOT_FOUND');

      const group = groupSnap.data();
      if (group.deletedAt) throw new AppError('El grupo fue eliminado', 'GROUP_DELETED');

      const dispensers = group.dispensers ?? [];

      if (dispenserStockGr !== undefined) {
        if (dispensers.length === 0) {
          throw new AppError(
            'Este grupo no usa dispenser. Solo se puede ajustar el stock de la bolsa.',
            'GROUP_WITHOUT_DISPENSER'
          );
        }
        const target = dispensers.find((d) => d.id === dispenserId);
        if (!target) {
          throw new AppError('El dispenser no existe en este grupo', 'DISPENSER_NOT_FOUND');
        }
        if (dispenserStockGr > target.capacityGr) {
          throw new AppError(
            `El stock declarado (${dispenserStockGr} g) excede la capacidad del dispenser "${target.name}" (${target.capacityGr} g)`,
            'EXCEEDS_CAPACITY'
          );
        }
      }

      const updates = { updatedAt: serverTimestamp() };
      const payload = { reason };

      if (mainStockGr !== undefined) {
        updates.mainStockGr = mainStockGr;
        payload.mainBefore = group.mainStockGr;
        payload.mainAfter = mainStockGr;
      }
      if (dispenserStockGr !== undefined) {
        const target = dispensers.find((d) => d.id === dispenserId);
        updates.dispensers = dispensers.map((d) =>
          d.id === dispenserId ? { ...d, stockGr: dispenserStockGr } : d
        );
        payload.dispenserId = dispenserId;
        payload.dispenserName = target.name;
        payload.dispenserBefore = target.stockGr ?? 0;
        payload.dispenserAfter = dispenserStockGr;
      }

      transaction.update(groupRef, updates);

      const logRef = doc(collection(db, COLLECTIONS.LOGS));
      transaction.set(logRef, {
        familyId: group.familyId,
        feedingGroupId,
        petId: null,
        userId: uid,
        type: LOG_TYPES.STOCK_ADJUSTMENT,
        timestamp: serverTimestamp(),
        payload,
      });
    });
  } catch (err) {
    if (err instanceof AppError) throw err;
    if (err?.cause instanceof AppError) throw err.cause;
    throw new FirestoreError('No se pudo ajustar el stock', err);
  }
};

// =====================================================================
// QUERIES Y SUSCRIPCIONES
// =====================================================================

/**
 * Suscripción reactiva a todas las mascotas activas (no eliminadas) de un hogar.
 * Retorna unsubscribe.
 */
export const subscribeToPets = (familyId, onChange, onError) => {
  if (!familyId) {
    queueMicrotask(() => onChange([]));
    return () => { };
  }

  const petsRef = collection(db, COLLECTIONS.PETS);
  // Importante: no usamos where('deletedAt', '==', null) porque Firestore
  // no indexa correctamente igualdad con null. Filtramos client-side.
  // Esto es aceptable para ≤10 mascotas por hogar.
  const q = query(petsRef, where('familyId', '==', familyId));

  return onSnapshot(
    q,
    (snapshot) => {
      const pets = snapshot.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((p) => !p.deletedAt);
      onChange(pets);
    },
    (err) => {
      console.error('[petService] Subscription error:', err);
      onError?.(new FirestoreError('Error en la suscripción de mascotas', err));
    }
  );
};

/**
 * Fetch puntual de una mascota.
 */
export const fetchPet = async (petId) => {
  try {
    const snap = await getDoc(doc(db, COLLECTIONS.PETS, petId));
    if (!snap.exists()) return null;
    return { id: snap.id, ...snap.data() };
  } catch (err) {
    throw new FirestoreError('No se pudo cargar la mascota', err);
  }
};

/**
 * Fetch de logs recientes de una mascota.
 *
 * La query se hace por feedingGroupId (no por petId) porque la bitácora del
 * detalle de la mascota incluye eventos del grupo (refill, confirmEmpty,
 * etc.) que afectan a todas las mascotas del grupo. Para filtrar solo
 * eventos pet-specific (feeding, manual_feeding, notas), el caller puede
 * filtrar el resultado por `log.petId === petId`.
 *
 * familyId es obligatorio: las reglas Firestore exigen isFamilyMember(resource.data.familyId)
 * y para queries Firestore necesita probar estáticamente que todos los resultados
 * pasarían la regla.
 */
export const fetchPetLogs = async (familyId, feedingGroupId, { limit: limitCount = 20 } = {}) => {
  if (!familyId) throw new AppError('familyId es requerido', 'INVALID_INPUT');
  if (!feedingGroupId) throw new AppError('feedingGroupId es requerido', 'INVALID_INPUT');
  try {
    const logsRef = collection(db, COLLECTIONS.LOGS);
    const q = query(
      logsRef,
      where('familyId', '==', familyId),
      where('feedingGroupId', '==', feedingGroupId),
      orderBy('timestamp', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.slice(0, limitCount).map((d) => ({ id: d.id, ...d.data() }));
  } catch (err) {
    if (err instanceof AppError) throw err;
    throw new FirestoreError('No se pudieron cargar los logs', err);
  }
};

/**
 * Fetch de logs recientes de un balde de agua (hydrationGroup).
 *
 * Análogo a fetchPetLogs pero para el dominio de hidratación. Toda la
 * bitácora del balde se filtra por hydrationGroupId — incluye watering
 * + NOTES de gestión (rename, interval change, activación/desactivación
 * de tracking en una pet, etc.).
 *
 * Requiere el composite index (familyId asc, hydrationGroupId asc,
 * timestamp desc) que se agregó a firestore.indexes.json en este sprint.
 */
export const fetchHydrationGroupLogs = async (
  familyId,
  hydrationGroupId,
  { limit: limitCount = 20 } = {}
) => {
  if (!familyId) throw new AppError('familyId es requerido', 'INVALID_INPUT');
  if (!hydrationGroupId) throw new AppError('hydrationGroupId es requerido', 'INVALID_INPUT');
  try {
    const logsRef = collection(db, COLLECTIONS.LOGS);
    const q = query(
      logsRef,
      where('familyId', '==', familyId),
      where('hydrationGroupId', '==', hydrationGroupId),
      orderBy('timestamp', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.slice(0, limitCount).map((d) => ({ id: d.id, ...d.data() }));
  } catch (err) {
    if (err instanceof AppError) throw err;
    throw new FirestoreError('No se pudieron cargar los logs del balde', err);
  }
};

/**
 * Fetch paginado de logs del hogar entero. Usado por la bitácora global (Fase 5).
 *
 * Paginación por cursor: el caller pasa el último log de la página previa como
 * `startAfterDoc` (DocumentSnapshot) para la siguiente. Si no hay siguiente
 * página, el array vuelve con menos de `limit` ítems.
 *
 * Decisión: no filtramos por tipo / pet / member en el server. Razones:
 *  - Firestore requeriría composite indexes por cada combinación (familyId + type
 *    + timestamp, familyId + petId + timestamp, etc.). Multiplicar índices por
 *    cada filtro es costoso operativamente y rinde poco para hogares chicos.
 *  - El volumen esperado es bajo (familia chica ≤ 10 mascotas). Traer 30 logs y
 *    filtrar client-side es aceptable.
 *  - Si el caso cambia, agregamos índices y movemos filtros al server sin
 *    cambiar la firma (los kwargs son extensibles).
 *
 * @returns {object} { logs: Array, nextCursor: DocumentSnapshot | null }
 */
export const fetchFamilyLogs = async (familyId, { limit: limitCount = 30, startAfterDoc = null } = {}) => {
  if (!familyId) throw new AppError('familyId es requerido', 'INVALID_INPUT');
  try {
    const logsRef = collection(db, COLLECTIONS.LOGS);
    const clauses = [
      where('familyId', '==', familyId),
      orderBy('timestamp', 'desc'),
    ];
    if (startAfterDoc) clauses.push(startAfter(startAfterDoc));
    clauses.push(fsLimit(limitCount));
    const q = query(logsRef, ...clauses);
    const snapshot = await getDocs(q);
    const logs = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
    const nextCursor = snapshot.docs.length === limitCount
      ? snapshot.docs[snapshot.docs.length - 1]
      : null;
    return { logs, nextCursor };
  } catch (err) {
    if (err instanceof AppError) throw err;
    throw new FirestoreError('No se pudieron cargar los logs del hogar', err);
  }
};

/**
 * Calcula estimaciones de autonomía combinando datos de la mascota
 * (ración diaria, comidas por día) con datos del grupo (stock, learning).
 * No es transaccional — es una computación derivada pura.
 *
 * Si el grupo sirve varias mascotas, las estimaciones de comidas restantes
 * son específicas para esta mascota (basadas en SU porción), pero el stock
 * disponible es compartido — comidas de otras mascotas también lo consumen.
 *
 * @returns {object} {
 *   strict: number,       // Comidas restantes según cálculo contable
 *   learned: number,      // Comidas restantes según aprendizaje (puede ser igual a strict)
 *   confidence: string,   // 'low' | 'medium' | 'high'
 *   daysRemaining: number | null,
 *   learningStatus: string
 * }
 */
export const getPetEstimations = (pet, group) => {
  if (!pet || !group) return null;

  const portionGr = computePortionGr(pet.dailyRationGr, pet.feedingsPerDay);

  // Stock disponible:
  //  - Con dispensers (1+): suma de stockGr de todos los dispensers del grupo.
  //  - Sin dispensers ([]): bolsa principal (la mascota come directo de ahí).
  const dispensers = group.dispensers ?? [];
  const usesDispenser = dispensers.length > 0;
  const availableStockGr = usesDispenser
    ? dispensers.reduce((acc, d) => acc + (d.stockGr ?? 0), 0)
    : (group.mainStockGr ?? 0);

  const { strict, learned, confidence } = estimateRemainingFeedings({
    dispenserStockGr: availableStockGr,
    portionGr,
    avgFeedingsPerRefill: group.avgFeedingsPerRefill,
    cycles: group.cycles || [],
    learningStatus: group.learningStatus,
  });

  const daysRemaining = estimateDaysRemaining({
    remainingFeedings: learned,
    feedingsPerDay: pet.feedingsPerDay,
  });

  return {
    strict,
    learned,
    confidence,
    daysRemaining,
    learningStatus: group.learningStatus,
    source: usesDispenser ? 'dispenser' : 'main_bag',
  };
};